import { useState, useEffect, useCallback } from 'react';
import { Search, Filter, X, Trash2, Sparkles } from 'lucide-react';
import { versionService, type Version, type PaginatedVersionsResponse, type VersionFiltersParams } from '../services/versionService';
import { useVersionFilters } from '../hooks/useVersionFilters';
import { useVersionSelection } from '../hooks/useVersionSelection';
import { VERSION_TYPES, VersionTipo } from '../config/versionTypes';
import { Button } from '../components/ui/Button';
import { VersionTableRow } from '../components/VersionTableRow';
import { VersionFormModal } from '../components/VersionFormModal';
import { ConfirmDeleteVersionModal } from '../components/ConfirmDeleteVersionModal';
import { VersionTypeBadge } from '../components/changelog/VersionTypeBadge';
import { VersionAreaBadge } from '../components/changelog/VersionAreaBadge';
import { useToast } from '../components/ui/Toast';
import { parseDateOnly } from '../utils/formatters';

export const GestionVersionesPage: React.FC = () => {
  const toast = useToast();
  const filters = useVersionFilters();
  const selection = useVersionSelection();

  const [response, setResponse] = useState<PaginatedVersionsResponse>({
    data: [],
    total: 0,
    page: 1,
    pageSize: 20,
    totalPages: 0
  });

  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [searchDebounceTimer, setSearchDebounceTimer] = useState<NodeJS.Timeout | null>(null);

  const [pendientes, setPendientes] = useState<Version[]>([]);
  const [loadingPendientes, setLoadingPendientes] = useState(true);
  const [publicandoCode, setPublicandoCode] = useState<string | null>(null);

  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteMode, setDeleteMode] = useState<'single' | 'multiple'>('single');
  const [selectedVersion, setSelectedVersion] = useState<Version | null>(null);
  const [versionsToDelete, setVersionsToDelete] = useState<Version[]>([]);

  const loadPendientes = useCallback(async () => {
    try {
      setLoadingPendientes(true);
      const data = await versionService.getVersionesPendientes();
      setPendientes(data);
    } catch {
      // no bloquea la página
    } finally {
      setLoadingPendientes(false);
    }
  }, []);

  useEffect(() => {
    loadVersions(1);
    loadPendientes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildFiltersParams = (): VersionFiltersParams => {
    const params: VersionFiltersParams = {};

    if (filters.searchTerm.trim()) {
      params.searchTerm = filters.searchTerm.trim();
    }

    if (filters.selectedTypes.length > 0) {
      params.tipos = filters.selectedTypes;
    }

    if (filters.fechaInicio) {
      params.fechaInicio = filters.fechaInicio;
    }

    if (filters.fechaFin) {
      params.fechaFin = filters.fechaFin;
    }

    return params;
  };

  const loadVersions = async (page: number = 1) => {
    try {
      setLoading(true);
      const filterParams = buildFiltersParams();
      const data = await versionService.getPaginated(page, 20, filterParams);
      setResponse(data);

      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error loading versions:', error);
      toast.error('Error al cargar las versiones');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (value: string) => {
    filters.setSearchTerm(value);

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    const timer = setTimeout(() => {
      loadVersions(1);
    }, 500);

    setSearchDebounceTimer(timer);
  };

  const handleFilterChange = () => {
    loadVersions(1);
  };

  const handleResetFilters = () => {
    filters.resetFilters();
    setTimeout(() => loadVersions(1), 0);
  };

  const handlePageChange = (newPage: number) => {
    selection.clearSelection();
    loadVersions(newPage);
  };

  const handleEdit = (version: Version) => {
    setSelectedVersion(version);
    setShowFormModal(true);
  };

  const handleDeleteSingle = (version: Version) => {
    setSelectedVersion(version);
    setVersionsToDelete([]);
    setDeleteMode('single');
    setShowDeleteModal(true);
  };

  const handleDeleteMultiple = () => {
    const selectedArray = selection.getSelectedArray();
    const versions = response.data.filter(v => selectedArray.includes(v.id));
    setVersionsToDelete(versions);
    setSelectedVersion(null);
    setDeleteMode('multiple');
    setShowDeleteModal(true);
  };

  const handlePublicar = async (versionCode: string) => {
    setPublicandoCode(versionCode);
    try {
      const result = await versionService.publicarVersionGrupo(versionCode);
      toast.success(`v${versionCode} publicada correctamente.`);
      if (result.githubReleaseUrl) {
        toast.success('GitHub Release creado exitosamente.');
      } else if (result.githubError) {
        toast.error(`GitHub Release no creado: ${result.githubError}`);
      }
      await Promise.all([loadPendientes(), loadVersions(response.page)]);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Error al publicar';
      toast.error(msg);
    } finally {
      setPublicandoCode(null);
    }
  };

  const handleFormSuccess = () => {
    loadVersions(response.page);
    loadPendientes();
    selection.clearSelection();
  };

  const handleDeleteSuccess = () => {
    selection.clearSelection();
    loadVersions(response.page);
  };

  const handleSelectAll = () => {
    const allIds = response.data.map(v => v.id);
    selection.toggleSelectAll(allIds);
  };

  const allSelected = response.data.length > 0 &&
    response.data.every(v => selection.isSelected(v.id));

  const gruposPendientes = versionService.groupByVersionCode(pendientes);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-slate-800 mb-2">Gestión de Versiones</h2>
          <p className="text-slate-600">Administra todas las versiones de la aplicación</p>
        </div>
      </div>

      {!loadingPendientes && gruposPendientes.size > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-amber-600" />
              <h3 className="font-semibold text-amber-900">
                {gruposPendientes.size === 1 ? '1 versión pendiente de publicación' : `${gruposPendientes.size} versiones pendientes de publicación`}
              </h3>
            </div>
            <div className="space-y-3">
              {[...gruposPendientes.entries()].map(([code, items]) => (
                <div key={code} className="bg-white rounded-lg border border-amber-200 px-4 py-3">
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-bold text-slate-900 text-sm">v{code}</span>
                        <span className="text-xs text-slate-400">{parseDateOnly(items[0].fecha).toLocaleDateString('es-CL')}</span>
                      </div>
                      <ul className="space-y-1">
                        {items.map(v => (
                          <li key={v.id} className="flex items-start gap-2">
                            <VersionTypeBadge tipo={v.tipo} />
                            <VersionAreaBadge area={v.area} />
                            <span className="text-sm text-slate-600 leading-5">{v.descripcion}</span>
                            <button
                              type="button"
                              onClick={() => handleEdit(v)}
                              className="shrink-0 text-xs text-slate-400 hover:text-slate-600 underline leading-5"
                            >
                              Editar
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                    <Button
                      onClick={() => handlePublicar(code)}
                      disabled={publicandoCode === code}
                      className="shrink-0"
                    >
                      {publicandoCode === code ? 'Publicando...' : 'Publicar'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
      )}

      <div className="bg-white rounded-xl shadow-md border border-slate-200 p-6">
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Buscar por código, descripción o tipo..."
              value={filters.searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full pl-12 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors font-semibold ${
              showFilters || filters.hasActiveFilters
                ? 'bg-blue-600 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Filter className="w-5 h-5" />
            Filtros
            {filters.hasActiveFilters && (
              <span className="ml-2 bg-white/30 px-2 py-1 rounded text-xs">Activos</span>
            )}
          </button>
        </div>

        {showFilters && (
          <div className="bg-slate-50 rounded-lg p-6 mb-6 space-y-4">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-3">
                Filtrar por Tipo
              </label>
              <div className="flex flex-wrap gap-3">
                {Object.keys(VERSION_TYPES).map((key) => {
                  const tipo = key as VersionTipo;
                  const isSelected = filters.selectedTypes.includes(tipo);
                  const config = VERSION_TYPES[tipo];

                  return (
                    <button
                      key={tipo}
                      onClick={() => {
                        filters.toggleType(tipo);
                        handleFilterChange();
                      }}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border-2 ${
                        isSelected
                          ? 'bg-blue-100 border-blue-500 text-blue-700'
                          : 'bg-white border-slate-300 text-slate-700 hover:border-slate-400'
                      }`}
                    >
                      {config.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha Inicio
                </label>
                <input
                  type="date"
                  value={filters.fechaInicio}
                  onChange={(e) => {
                    filters.setFechaInicio(e.target.value);
                    handleFilterChange();
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Fecha Fin
                </label>
                <input
                  type="date"
                  value={filters.fechaFin}
                  onChange={(e) => {
                    filters.setFechaFin(e.target.value);
                    handleFilterChange();
                  }}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                />
              </div>
            </div>

            {filters.hasActiveFilters && (
              <button
                onClick={handleResetFilters}
                className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm"
              >
                <X className="w-4 h-4" />
                Limpiar filtros
              </button>
            )}
          </div>
        )}

        <div className="overflow-x-auto">
          {loading && (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
            </div>
          )}
          {!loading && response.data.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              {filters.hasActiveFilters ? (
                <>
                  <p className="mb-2">No se encontraron versiones</p>
                  <button
                    onClick={handleResetFilters}
                    className="text-blue-600 hover:text-blue-700 font-medium text-sm"
                  >
                    Limpiar filtros
                  </button>
                </>
              ) : (
                <>
                  <p className="mb-2">No hay versiones registradas</p>
                  <p className="text-sm">Crea la primera versión usando el botón de arriba</p>
                </>
              )}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={handleSelectAll}
                      className="w-4 h-4 text-blue-600 border-slate-300 rounded focus:ring-2 focus:ring-blue-500 cursor-pointer"
                    />
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">
                    Versión
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">
                    Tipo
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">
                    Descripción
                  </th>
                  <th className="px-6 py-3 text-left text-sm font-semibold text-slate-700">
                    Fecha
                  </th>
                  <th className="px-6 py-3 text-right text-sm font-semibold text-slate-700">
                    Acciones
                  </th>
                </tr>
              </thead>
              <tbody>
                {[...versionService.groupByVersionCode(response.data).values()].flatMap((grupo) =>
                  grupo.map((version, index) => (
                    <VersionTableRow
                      key={version.id}
                      version={version}
                      isSelected={selection.isSelected(version.id)}
                      onSelect={selection.toggleSelection}
                      onEdit={handleEdit}
                      onDelete={handleDeleteSingle}
                      showVersionCell={index === 0}
                      versionRowSpan={grupo.length}
                      isGroupStart={index === 0}
                    />
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>

        {response.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-6 border-t border-slate-200">
            <div className="text-sm text-slate-600">
              Página {response.page} de {response.totalPages} ({response.total} versiones)
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => handlePageChange(response.page - 1)}
                disabled={response.page === 1}
              >
                Anterior
              </Button>
              <Button
                variant="outline"
                onClick={() => handlePageChange(response.page + 1)}
                disabled={response.page === response.totalPages}
              >
                Siguiente
              </Button>
            </div>
          </div>
        )}
      </div>

      {selection.selectedCount > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-blue-600 text-white shadow-2xl z-50">
          <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
            <span className="font-semibold">
              {selection.selectedCount} {selection.selectedCount === 1 ? 'versión seleccionada' : 'versiones seleccionadas'}
            </span>
            <div className="flex items-center gap-3">
              <Button
                variant="danger"
                onClick={handleDeleteMultiple}
              >
                <Trash2 className="w-4 h-4" />
                Eliminar seleccionados
              </Button>
              <Button
                variant="secondary"
                onClick={selection.clearSelection}
              >
                Cancelar selección
              </Button>
            </div>
          </div>
        </div>
      )}

      <VersionFormModal
        key={selectedVersion?.id ?? 'new'}
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        onSuccess={handleFormSuccess}
        version={selectedVersion}
      />

      <ConfirmDeleteVersionModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSuccess={handleDeleteSuccess}
        mode={deleteMode}
        version={selectedVersion}
        versions={versionsToDelete}
      />
    </div>
  );
};
